import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import axios from 'axios';

import AccountCreationFields from '../../components/auth/AccountCreationFields';
import PhoneVerificationModal from '../../components/auth/PhoneVerificationModal';
import useAccountCreation from '../../hooks/useAccountCreation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RegisterPage() {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    password: '', confirm_password: '',
  });
  // Inline field-level validation errors. Populated by validate() when
  // the user submits; cleared per-field as they edit so mistakes fade.
  const [errors, setErrors] = useState({});
  const onField = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setErrors((prev) => {
      if (!prev[field] && !(field === 'password' && prev.confirm_password) && !(field === 'confirm_password' && prev.password)) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      // Password/confirm are linked — retyping either should clear both.
      if (field === 'password' || field === 'confirm_password') {
        delete next.password;
        delete next.confirm_password;
      }
      return next;
    });
  };

  const [agreed, setAgreed] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);

  // Referral code
  const [referralCode, setReferralCode] = useState('');
  const [referralStatus, setReferralStatus] = useState(null);
  const checkReferralCode = async (code) => {
    if (!code.trim()) { setReferralStatus(null); return; }
    setReferralStatus('checking');
    try {
      const res = await axios.post(`${API_URL}/api/referral/apply-code`, {
        code: code.trim(),
        email: form.email || 'check@temp.com',
      });
      if (res.data.valid) {
        setReferralStatus('valid');
        toast.success(`Referral from ${res.data.referrer_name} applied!`);
      }
    } catch { setReferralStatus('invalid'); }
  };

  const account = useAccountCreation();

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = 'First name is required';
    if (!form.last_name.trim()) errs.last_name = 'Last name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = 'Please enter a valid email address';
    const digits = (form.phone || '').replace(/\D/g, '');
    if (!digits) errs.phone = 'Mobile phone is required';
    else if (digits.length < 10) errs.phone = 'Please enter a valid 10-digit mobile number';
    if (!form.password) errs.password = 'Password is required';
    else if (form.password.length < 8) errs.password = 'Password must be at least 8 characters';
    if (!form.confirm_password) errs.confirm_password = 'Please confirm your password';
    else if (form.password && form.password !== form.confirm_password) errs.confirm_password = 'Passwords do not match';
    if (!agreed) errs.agreed = 'You must agree to the Terms & Privacy Policy to continue';
    return errs;
  };

  const redirectAfterSignup = () => {
    const hasCampContext = localStorage.getItem('pendingCampRegistration');
    window.location.href = hasCampContext ? '/parent?from=camp' : '/parent';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      toast.error('Please fix the highlighted fields');
      setTimeout(() => {
        const first = Object.keys(errs)[0];
        const el = document.querySelector(`[data-register-field="${first}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }
    setErrors({});
    await account.startVerification({ email: form.email, phone: form.phone });
  };

  const onVerifyEmailCode = async () => {
    const data = await account.verifyEmailAndCreate({
      code: account.emailCode,
      ...form,
      sms_consent: smsConsent,
      referral_code: referralCode.trim() || undefined,
    });
    if (data?.access_token) redirectAfterSignup();
  };

  const onPhoneVerified = async () => {
    account.setShowPhoneVerify(false);
    try {
      const data = await account.createAccount({
        ...form,
        sms_consent: smsConsent,
        referral_code: referralCode.trim() || undefined,
      });
      if (data?.access_token) {
        toast.success('Account created! Welcome to Ready Set STEM!');
        redirectAfterSignup();
      }
    } catch (err) {
      const d = err.response?.data;
      // Map common server-side field errors back to inline state so the user
      // can see exactly which field to fix (e.g., duplicate email).
      const serverErrors = {};
      if (d && typeof d === 'object') {
        for (const key of ['email', 'phone', 'password', 'first_name', 'last_name']) {
          if (d[key]) serverErrors[key] = Array.isArray(d[key]) ? d[key][0] : String(d[key]);
        }
      }
      if (Object.keys(serverErrors).length > 0) {
        setErrors(serverErrors);
        toast.error('Please fix the highlighted fields');
        setTimeout(() => {
          const first = Object.keys(serverErrors)[0];
          const el = document.querySelector(`[data-register-field="${first}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
        return;
      }
      let msg = d?.detail;
      if (!msg && d && typeof d === 'object') {
        msg = Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v[0] : v}`).join(', ');
      }
      toast.error(msg || 'Registration failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rsc-navy to-rsc-navy-dark flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img
              src="/brand/rss-logo-horizontal.png"
              alt="Ready Set STEM"
              className="h-16 mx-auto mb-4"
            />
          </Link>
          <h1 className="text-2xl font-heading font-bold text-white">Create Your Account</h1>
          <p className="text-white/70 mt-1">Join Ready Set STEM and start your child's STEM journey</p>
        </div>

        <Card className="shadow-2xl">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              <AccountCreationFields
                values={form}
                onChange={onField}
                errors={errors}
                verifyMethod={account.verifyMethod}
                setVerifyMethod={account.setVerifyMethod}
                agreed={agreed}
                setAgreed={setAgreed}
                smsConsent={smsConsent}
                setSmsConsent={setSmsConsent}
                referralSlot={(
                  <div className="space-y-2">
                    <Label>Referral Code (optional)</Label>
                    <div className="relative">
                      <Input
                        value={referralCode}
                        onChange={(e) => { setReferralCode(e.target.value); setReferralStatus(null); }}
                        onBlur={() => checkReferralCode(referralCode)}
                        placeholder="Enter referral code"
                        className={
                          referralStatus === 'valid' ? 'border-green-500 pr-10'
                            : referralStatus === 'invalid' ? 'border-red-500 pr-10'
                            : ''
                        }
                        data-testid="referral-code-input"
                      />
                      {referralStatus === 'valid' &&
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-sm font-medium">&#10003;</span>}
                      {referralStatus === 'invalid' &&
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 text-xs">Invalid</span>}
                      {referralStatus === 'checking' &&
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
                    </div>
                  </div>
                )}
              />

              <Button type="submit" className="w-full" disabled={account.sending} data-testid="register-submit-btn">
                {account.sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {account.sending
                  ? 'Sending Verification Code...'
                  : account.verifyMethod === 'phone' ? 'Continue — Verify Phone' : 'Continue — Verify Email'}
              </Button>
            </form>

            {/* Email OTP step */}
            {account.emailStep === 'verify' && (
              <div className="mt-4 p-4 rounded-xl bg-blue-50 border border-blue-200">
                <h3 className="font-bold text-gray-900 mb-1">Verify Your Email</h3>
                <p className="text-sm text-gray-500 mb-3">
                  Enter the 6-digit code sent to {form.email}
                </p>
                <div className="space-y-3">
                  <Input
                    type="text"
                    maxLength={6}
                    value={account.emailCode}
                    onChange={(e) => account.setEmailCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="Enter 6-digit code"
                    className="text-center text-2xl tracking-[0.5em] font-mono h-14"
                    inputMode="numeric"
                    autoFocus
                    data-testid="email-verification-code"
                  />
                  <Button
                    onClick={onVerifyEmailCode}
                    className="w-full bg-rsc-navy"
                    disabled={account.verifying || account.emailCode.length !== 6}
                    data-testid="verify-create-btn"
                  >
                    {account.verifying
                      ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifying...</>)
                      : 'Verify & Create Account'}
                  </Button>
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => account.setEmailStep('form')}
                      className="text-xs text-gray-500 hover:underline"
                    >
                      Back to form
                    </button>
                    <button
                      onClick={() => account.startVerification({ email: form.email, phone: form.phone })}
                      className="text-xs text-blue-600 hover:underline"
                      disabled={account.sending}
                    >
                      Resend code
                    </button>
                  </div>
                </div>
              </div>
            )}

            <p className="text-center text-sm text-muted-foreground mt-4">
              Already have an account? <Link to="/login" className="text-primary font-semibold">Sign In</Link>
            </p>
          </CardContent>
        </Card>
      </div>

      {/* SMS-first verification modal */}
      <PhoneVerificationModal
        open={account.showPhoneVerify}
        phone={form.phone}
        purpose="registration"
        allowSkip={false}
        title="Verify your phone to create your account"
        description="We just texted a 6-digit code. Enter it below and we'll create your account."
        onVerified={onPhoneVerified}
        onSkip={() => account.setShowPhoneVerify(false)}
        onClose={() => account.setShowPhoneVerify(false)}
      />
    </div>
  );
}
